import React from "react";
import { motion } from "framer-motion";

/**
 * Animated brand constellation — an SVG replica of the PIS mark:
 * a swirl of dots orbiting a 4-point polestar sparkle.
 *
 * The whole constellation rotates very slowly, each dot pulses opacity
 * on a staggered loop, and the central star twinkles with a soft glow.
 */

// Hand-placed dots to approximate the logo distribution.
// {x, y, r, tone: "light" | "mid" | "bright"}
const DOTS = [
  // outer ring (larger, light)
  { x: 250, y: 60, r: 16, tone: "light" },
  { x: 345, y: 85, r: 20, tone: "light" },
  { x: 420, y: 165, r: 18, tone: "light" },
  { x: 445, y: 265, r: 20, tone: "light" },
  { x: 420, y: 360, r: 14, tone: "light" },
  { x: 355, y: 425, r: 20, tone: "light" },
  { x: 260, y: 450, r: 18, tone: "light" },
  { x: 170, y: 435, r: 16, tone: "light" },
  { x: 85, y: 390, r: 14, tone: "light" },
  { x: 55, y: 300, r: 18, tone: "light" },
  { x: 70, y: 205, r: 20, tone: "light" },
  { x: 125, y: 115, r: 18, tone: "light" },
  { x: 190, y: 70, r: 14, tone: "light" },

  // mid ring (medium, light-blue tint)
  { x: 250, y: 125, r: 12, tone: "light" },
  { x: 330, y: 155, r: 10, tone: "mid" },
  { x: 365, y: 230, r: 14, tone: "light" },
  { x: 355, y: 320, r: 12, tone: "light" },
  { x: 300, y: 370, r: 14, tone: "mid" },
  { x: 220, y: 395, r: 10, tone: "mid" },
  { x: 145, y: 360, r: 12, tone: "light" },
  { x: 110, y: 285, r: 14, tone: "light" },
  { x: 130, y: 200, r: 10, tone: "mid" },
  { x: 185, y: 150, r: 12, tone: "mid" },

  // inner ring (small, brighter)
  { x: 260, y: 190, r: 7, tone: "bright" },
  { x: 310, y: 220, r: 7, tone: "bright" },
  { x: 325, y: 275, r: 6, tone: "bright" },
  { x: 305, y: 320, r: 7, tone: "bright" },
  { x: 255, y: 345, r: 7, tone: "bright" },
  { x: 200, y: 325, r: 6, tone: "bright" },
  { x: 180, y: 275, r: 7, tone: "bright" },
  { x: 195, y: 225, r: 6, tone: "bright" },
  { x: 230, y: 200, r: 5, tone: "bright" },
  { x: 290, y: 200, r: 5, tone: "bright" },
  { x: 215, y: 350, r: 5, tone: "bright" },
  { x: 285, y: 345, r: 5, tone: "bright" },
];

const toneFill = {
  light: "rgba(173, 199, 229, 0.35)",   // soft icy blue
  mid: "rgba(120, 170, 255, 0.75)",    // brighter mid blue
  bright: "rgba(70, 140, 255, 1)",     // vivid brand blue
};

export default function BrandConstellation({
  className = "",
  rotateDuration = 90,
  withGlow = true,
  size = 500,
}) {
  return (
    <div
      className={className}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <motion.svg
        viewBox="0 0 500 500"
        width="100%"
        height="100%"
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: rotateDuration, ease: "linear" }}
        style={{ overflow: "visible" }}
      >
        <defs>
          <radialGradient id="pisStarGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(70, 140, 255, 0.55)" />
            <stop offset="60%" stopColor="rgba(0, 122, 255, 0.1)" />
            <stop offset="100%" stopColor="rgba(0, 122, 255, 0)" />
          </radialGradient>
          <linearGradient id="pisStarFill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4a9bff" />
            <stop offset="100%" stopColor="#1f6fff" />
          </linearGradient>
          <filter id="pisStarBlur" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>

        {/* soft halo behind the star */}
        {withGlow && (
          <motion.circle
            cx="250"
            cy="250"
            r="160"
            fill="url(#pisStarGlow)"
            animate={{ opacity: [0.55, 0.9, 0.55] }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
          />
        )}

        {/* orbiting dots */}
        {DOTS.map((d, i) => (
          <motion.circle
            key={i}
            cx={d.x}
            cy={d.y}
            r={d.r}
            fill={toneFill[d.tone]}
            initial={{ opacity: 0 }}
            animate={{
              opacity: d.tone === "bright" ? [0.6, 1, 0.6] : [0.35, 0.75, 0.35],
              scale: [1, 1.08, 1],
            }}
            transition={{
              repeat: Infinity,
              duration: 3 + (i % 5) * 0.5,
              delay: (i % 11) * 0.18,
              ease: "easeInOut",
            }}
            style={{ transformOrigin: `${d.x}px ${d.y}px` }}
          />
        ))}

        {/* 4-point polestar sparkle at center */}
        <motion.g
          style={{ transformOrigin: "250px 250px" }}
          animate={{ rotate: [0, 360] }}
          transition={{ repeat: Infinity, duration: 40, ease: "linear" }}
        >
          <motion.path
            d="M250,180 C258,232 268,242 320,250 C268,258 258,268 250,320 C242,268 232,258 180,250 C232,242 242,232 250,180 Z"
            fill="url(#pisStarFill)"
            animate={{ opacity: [0.85, 1, 0.85], scale: [1, 1.06, 1] }}
            transition={{ repeat: Infinity, duration: 3.2, ease: "easeInOut" }}
            style={{ transformOrigin: "250px 250px" }}
          />
          {/* soft glow copy behind */}
          <motion.path
            d="M250,180 C258,232 268,242 320,250 C268,258 258,268 250,320 C242,268 232,258 180,250 C232,242 242,232 250,180 Z"
            fill="rgba(70, 140, 255, 0.55)"
            filter="url(#pisStarBlur)"
            animate={{ opacity: [0.5, 0.95, 0.5] }}
            transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
          />
        </motion.g>
      </motion.svg>
    </div>
  );
}
